import React, { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import clsx from "clsx";

import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Close from "@material-ui/icons/Close";
import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import Activity from "components/Activity";
import DaysOff from "components/DaysOff";
import ErrorBoundary from "components/ErrorBoundary";
import InfoPanelsGrid from "components/InfoPanelsGrid";
import Loader from "components/Loader";
import Location from "components/Location";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import TabsComponent from "components/Tabs";
import TitledContainer from "components/TitledContainer";
import FacilitySelect from "components/FacilitySelect";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import ITab from "interfaces/Tab";
import Header from "./Header";
import Information from "./Information";
import PreferredCondition from "./PreferredCondition";
import UpcomingEvents from "./UpcomingEvents";
import Comments from "./Comments";
import { TRAINER_HORSE_GET_OPTIONS, trainerTabsList } from "./options";
import { useCurrentHorseDetails } from "../useCurrentHorseDetails";
import { setMapPopupState } from "store/actions/trainerHorseDetailsPage";
import MatchedRacesPopover from "./MatchedRacesPopover";
import useStyles from "./styles";
import { getTabFromPath } from "../helper";

const TrainerHorseDetailsPage = () => {
  const match = useRouteMatch<{ horseId: string }>(routes.horseDetails.path);

  const classes = useStyles();
  const matchesDownSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const matchesDownXs = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );

  const rootRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();

  const [tabs, setTabs] = useState<ITab[]>(trainerTabsList);
  const [activeTab, setActiveTab] = useState<ITab>(
    getTabFromPath(history.location.pathname, trainerTabsList)
  );
  const [selectedMapFacility, setSelectedMapFacility] =
    useState<racehorse360.IFacility>(null);
  const [isOpenMatchedRacesPopover, setIsOpenMatchedRacesPopover] =
    useState<boolean>(false);

  const { isOpenPreferredConditions, isMapFullscreen, selectedFacilityIds } =
    useAppSelector(
      state => ({
        isOpenPreferredConditions:
          state.trainerHorseDetailsPage.preferredConditionsState,
        isMapFullscreen: state.trainerHorseDetailsPage.mapPopupState,
        selectedFacilityIds: state.queriesConfig?.selectedFacilityIds
      }),
      shallowEqual
    );

  const isSingleSpecificFacilitySelected = selectedFacilityIds?.length === 1;

  const {
    useGetFacility,
    usePullHorseAggregatedComments,
    usePullRaceConditionsForHorse
  } = useRacehorse360Api();

  const {
    data: selectedFacilityInHorsesToDisplay,
    isLoading: isSelectedFacilityInHorsesToDisplayLoading
  } = useGetFacility(
    {
      id: selectedFacilityIds?.[0],
      getOptions: {
        select: ["trainingFacilities"]
      }
    },
    {
      enabled: isSingleSpecificFacilitySelected
    }
  );

  const trainingCentersOfSelectedFacility =
    selectedFacilityInHorsesToDisplay?.trainingFacilities?.map(
      item => item.id
    ) ?? [];

  const { currentHorse, isHorseLoading, updateCurrentHorse } =
    useCurrentHorseDetails({
      selectedFields: TRAINER_HORSE_GET_OPTIONS
    });

  const horseCurrentFacility = currentHorse?.currentFacility;

  const horseId = currentHorse?.id;
  const horseFacilityId =
    currentHorse?.barn?.facility?.id || currentHorse?.currentFacility?.id;

  const {
    data: horseMatchesData,
    isLoading: isMatchesLoading,
    refetch: refetchHorseMatchesData
  } = usePullRaceConditionsForHorse(
    `${horseId}-matches`,
    {
      horseId: horseId
    },
    {
      enabled: Boolean(horseId)
    }
  );
  const matchedRaces = horseMatchesData?.raceConditions;

  const {
    data: horseAggregatedCommentsData,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
    isFetching: isCommentsFetching
  } = usePullHorseAggregatedComments(
    {
      query: {
        horseId: currentHorse?.id
      }
    },
    {
      enabled: Boolean(horseId),
      onSuccess: data => {
        const hasUnreadComments = data.horseAggregatedComments.some(
          comment => !comment.isRead
        );
        const newTabs = tabs.map(tab => {
          const newTab = {
            ...tab,
            path: tab.path.replace(":horseId", match.params.horseId)
          };

          return tab.name === "Comments"
            ? { ...newTab, isUpdated: hasUnreadComments }
            : newTab;
        });

        setTabs(newTabs);
      }
    }
  );
  const comments = horseAggregatedCommentsData?.horseAggregatedComments;

  const isLoading = [
    isHorseLoading,
    isMatchesLoading,
    isCommentsLoading,
    isSelectedFacilityInHorsesToDisplayLoading
  ].some(item => item);

  const handleTabChange = (tab: ITab) => {
    setActiveTab(tab);
  };

  const handleMapOpen = () => {
    dispatch(setMapPopupState(true));
  };

  const handleMapClose = () => {
    dispatch(setMapPopupState(false));
  };

  const handleMapFullscreenClick = () => {
    dispatch(setMapPopupState(true));
  };

  const handleChangeFacility = (facility: racehorse360.IFacility) => {
    setSelectedMapFacility(facility);
  };

  const handleOpenMatchedRacesPopover = useCallback(() => {
    setIsOpenMatchedRacesPopover(true);
  }, [setIsOpenMatchedRacesPopover]);

  const handleCloseMatchedRacesPopover = useCallback(() => {
    setIsOpenMatchedRacesPopover(false);
  }, [setIsOpenMatchedRacesPopover]);

  const handleHorseUpdateOnMap = (
    patch: racehorse360.IHorse,
    horseId: string
  ) => {
    if (currentHorse?.id === horseId) {
      updateCurrentHorse(patch);
    }
  };

  useEffect(() => {
    const shouldRedirectToHorseListPage = [
      currentUser.isTrainer,
      currentHorse,
      isSingleSpecificFacilitySelected,
      selectedFacilityInHorsesToDisplay,
      ![
        selectedFacilityInHorsesToDisplay?.id,
        ...trainingCentersOfSelectedFacility
      ].includes(horseFacilityId)
    ].every(Boolean);

    if (shouldRedirectToHorseListPage) {
      history.push(routes.horseList.path);
    }
  }, [selectedFacilityIds, horseFacilityId]);

  useEffect(() => {
    const list = tabs.map(item => ({
      ...item,
      path: item.path.replace(":horseId", match.params.horseId)
    }));
    setTabs(list);
    return () => {
      dispatch(setMapPopupState(false));
    };
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  if (!currentHorse) return null;

  return (
    <AppPage className={clsx({ [classes.rootBorder]: isMapFullscreen })}>
      <ErrorBoundary>
        {!isOpenPreferredConditions && (
          <AppPageHeader
            className={clsx(classes.appPageHeader, {
              [classes.noBorder]: isMapFullscreen
            })}
          >
            {isMapFullscreen ? (
              <div className={classes.mapHeader}>
                <Typography component={"b"} className={classes.mapHeaderTitle}>
                  Location
                </Typography>
                <div className={classes.mapFacilitySelectWrapper}>
                  <ErrorBoundary>
                    <FacilitySelect
                      isActive={true}
                      selectedFacility={
                        selectedMapFacility || horseCurrentFacility
                      }
                      onChange={handleChangeFacility}
                    />
                  </ErrorBoundary>
                </div>
                <IconButton
                  size={matchesDownXs ? "small" : "medium"}
                  onClick={handleMapClose}
                >
                  <Close />
                </IconButton>
              </div>
            ) : (
              <>
                <Header horse={currentHorse} />
                {matchesDownSm && (
                  <TabsComponent
                    tabs={tabs}
                    selected={activeTab}
                    onChange={handleTabChange}
                    className={classes.appPageHeaderTabs}
                  />
                )}
              </>
            )}
          </AppPageHeader>
        )}
      </ErrorBoundary>
      <AppPageContent
        className={clsx(
          classes.appPageContentContainer,
          classes[activeTab.value],
          {
            [classes.progress]: isLoading
          }
        )}
      >
        {isMapFullscreen ? (
          <ErrorBoundary>
            <Location
              currentHorse={currentHorse}
              onOpen={handleMapOpen}
              onClose={handleMapClose}
              selectedFacility={selectedMapFacility || horseCurrentFacility}
              onHorseUpdate={handleHorseUpdateOnMap}
            />
          </ErrorBoundary>
        ) : (
          <div className={classes["grid-container"]}>
            <div className={classes["grid-details-and-days"]}>
              <div
                className={clsx(
                  classes["grid-info-panels"],
                  classes[tabs[0].value]
                )}
              >
                <TitledContainer title={"Details and Activity"}>
                  <ErrorBoundary>
                    <InfoPanelsGrid horse={currentHorse} />
                  </ErrorBoundary>
                </TitledContainer>
              </div>
              <div
                className={clsx(
                  classes["grid-days-off"],
                  classes[tabs[1].value]
                )}
              >
                <TitledContainer
                  title={matchesDownSm ? "Race / Work Days Off" : "Days Off"}
                >
                  <ErrorBoundary>
                    <DaysOff horse={currentHorse} showViewHorseButton={false} />
                  </ErrorBoundary>
                </TitledContainer>
              </div>
            </div>
            <div
              className={clsx(classes["grid-activity"], classes[tabs[1].value])}
            >
              <ErrorBoundary>
                <Activity horse={currentHorse} rootRef={rootRef} />
              </ErrorBoundary>
            </div>
            <div className={classes["grid-info"]}>
              <div
                className={clsx(
                  classes["grid-information"],
                  classes[tabs[0].value]
                )}
              >
                <TitledContainer title={"Information"}>
                  <ErrorBoundary>
                    <Information
                      selectedFacilityId={horseCurrentFacility?.id}
                    />
                  </ErrorBoundary>
                </TitledContainer>
              </div>
              <div
                className={clsx(
                  classes["grid-location"],
                  classes[tabs[0].value]
                )}
              >
                <TitledContainer title={"Location"}>
                  <ErrorBoundary>
                    <Location
                      currentHorse={currentHorse}
                      onFullscreenClick={handleMapFullscreenClick}
                      isPreview
                      selectedFacility={horseCurrentFacility}
                      onHorseUpdate={handleHorseUpdateOnMap}
                    />
                  </ErrorBoundary>
                </TitledContainer>
              </div>
            </div>
            <div className={classes["grid-right-rail"]}>
              <div
                className={clsx(
                  classes["grid-preferred-condition"],
                  classes[tabs[0].value]
                )}
              >
                <PreferredCondition
                  horse={
                    currentHorse || {
                      id: match.params.horseId
                    }
                  }
                  matchedRaces={matchedRaces}
                  onOpenMatchedRacesPopover={handleOpenMatchedRacesPopover}
                  refetchMatchedRaces={refetchHorseMatchesData}
                />
              </div>
              <div
                className={clsx(
                  classes["grid-upcoming-events"],
                  classes[tabs[1].value]
                )}
              >
                <ErrorBoundary>
                  <UpcomingEvents horse={currentHorse} />
                </ErrorBoundary>
              </div>
              <div
                className={clsx(
                  classes["grid-comments"],
                  classes[tabs[2].value]
                )}
              >
                <ErrorBoundary>
                  <Comments
                    horse={currentHorse}
                    commentsList={comments}
                    refetchComments={refetchComments}
                    isCommentsFetching={isCommentsFetching}
                  />
                </ErrorBoundary>
              </div>
            </div>
          </div>
        )}
      </AppPageContent>
      <MatchedRacesPopover
        isOpen={isOpenMatchedRacesPopover}
        races={matchedRaces}
        onClose={handleCloseMatchedRacesPopover}
      />
    </AppPage>
  );
};

export default TrainerHorseDetailsPage;
